import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { LINKS } from "../lib/config";
import Nav from "../components/Nav";
import CalendlyModal from "../components/CalendlyModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

export default function ArticlePage() {
    const { id } = useParams();
    const [post, setPost] = useState(null);
    const [status, setStatus] = useState("loading");
    const [calOpen, setCalOpen] = useState(false);
    const onBook = () => setCalOpen(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        axios
            .get(`${API}/blog/${id}`)
            .then((r) => {
                setPost(r.data);
                setStatus("ok");
            })
            .catch(() => setStatus("error"));
    }, [id]);

    if (status === "loading") {
        return (
            <div className="min-h-screen bg-powder flex items-center justify-center">
                <p className="font-serif text-2xl text-ink-muted animate-pulse">Caricamento…</p>
            </div>
        );
    }

    if (status === "error" || !post) {
        return (
            <div className="min-h-screen bg-powder flex flex-col items-center justify-center gap-6 px-5 text-center">
                <h1 className="font-serif text-4xl">Articolo non trovato</h1>
                <Link to="/" className="btn-fuchsia" data-testid="article-back-home">Torna alla home</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-powder text-ink" data-testid="article-page">
            <div className="grain-overlay" />
            <Nav onBook={onBook} variant="solid" />

            {/* Hero image */}
            <div className="relative h-[45vh] md:h-[60vh] w-full overflow-hidden bg-ink">
                <motion.img
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.4, ease: [0.65, 0, 0.35, 1] }}
                    src={resolveImg(post.image_url)}
                    alt={post.title}
                    className="w-full h-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
            </div>

            {/* Article body */}
            <article className="max-w-3xl mx-auto px-5 md:px-8 -mt-20 md:-mt-28 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="bg-white p-7 md:p-14 shadow-xl"
                >
                    <span className="inline-block bg-fuchsia text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1 rounded-full mb-6">
                        {post.category}
                    </span>
                    <h1 className="font-serif text-4xl md:text-6xl font-bold leading-[0.98] mb-6">
                        {post.title}
                    </h1>
                    <p className="font-sans text-lg md:text-xl text-ink-muted italic leading-relaxed mb-10 pb-10 border-b border-ink/10">
                        {post.excerpt}
                    </p>

                    <div className="font-sans text-base md:text-lg leading-relaxed text-ink space-y-5">
                        {(post.content || "")
                            .split("\n")
                            .map((l) => l.trim())
                            .filter((l) => l)
                            .map((line, i) => {
                                if (line.startsWith("## ")) {
                                    return (
                                        <h2 key={i} className="font-serif text-2xl md:text-3xl text-ink pt-4">
                                            {line.replace(/^##\s+/, "")}
                                        </h2>
                                    );
                                }
                                if (line.startsWith("- ")) {
                                    return (
                                        <div key={i} className="flex gap-3 pl-1">
                                            <span className="text-fuchsia mt-1.5 shrink-0">◆</span>
                                            <span>{line.replace(/^-\s+/, "")}</span>
                                        </div>
                                    );
                                }
                                return <p key={i}>{line}</p>;
                            })}
                        {!post.content?.trim() && (
                            <p className="text-ink-muted italic">Contenuto in arrivo…</p>
                        )}
                    </div>
                </motion.div>

                {/* CTA */}
                <div className="mt-12 md:mt-16 mb-24 text-center">
                    <p className="font-serif text-2xl md:text-3xl mb-6">Vuoi approfondire?</p>
                    <a
                        href={LINKS.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="article-instagram-cta"
                        className="btn-fuchsia"
                    >
                        <Instagram size={18} /> Seguimi su Instagram
                    </a>
                </div>
            </article>
            <CalendlyModal open={calOpen} onOpenChange={setCalOpen} />
        </div>
    );
}
